import * as React from 'react';
import { cn } from '@/lib/utils';

export type CheckboxVisualState = 'checked' | 'unchecked' | 'mixed';

export interface CheckboxProps extends React.InputHTMLAttributes<HTMLInputElement> {
    onCheckedChange?: (checked: boolean) => void;
    visualState?: CheckboxVisualState;
}

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
    ({ className, onCheckedChange, visualState, style, ...props }, ref) => {
        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            if (onCheckedChange) {
                onCheckedChange(e.target.checked);
            }
            if (props.onChange) {
                props.onChange(e);
            }
        };
        const resolvedVisualState: CheckboxVisualState =
            visualState ?? (props.checked === true ? 'checked' : 'unchecked');
        const backgroundImageByVisualState: Record<CheckboxVisualState, string> = {
            checked:
                "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='none' stroke='white' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M3.5 8.5l2.5 2.5 6-6'/%3E%3C/svg%3E\")",
            unchecked: 'none',
            mixed:
                "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='none' stroke='%231d4ed8' stroke-linecap='round' stroke-width='2' d='M4 8h8'/%3E%3C/svg%3E\")",
        };
        const visualStateClassName: string =
            resolvedVisualState === 'checked'
                ? 'border-blue-600 bg-blue-600'
                : resolvedVisualState === 'mixed'
                  ? 'border-sky-300 bg-sky-200/70'
                  : 'border-gray-300 bg-white';

        return (
            <input
                type="checkbox"
                className={cn(
                    'h-4 w-4 appearance-none rounded border bg-center bg-no-repeat focus:ring-blue-500',
                    visualStateClassName,
                    className
                )}
                data-visual-state={resolvedVisualState}
                ref={ref}
                {...props}
                style={{
                    backgroundImage: backgroundImageByVisualState[resolvedVisualState],
                    backgroundSize: '0.75rem 0.75rem',
                    ...style,
                }}
                onChange={handleChange}
            />
        );
    }
);
Checkbox.displayName = 'Checkbox';

export { Checkbox };
